import React, { useState } from 'react';
import { Sparkles, Loader2, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Sweeps dead and throwaway addresses off the list so send stats mean
 *  something. Scan first, tick what to clear, then remove - every removal is
 *  written to the removal log. */
const ListCleanupCard = ({ token, onChanged }) => {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [picked, setPicked] = useState([]);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [open, setOpen] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const runScan = async () => {
    setBusy(true); setError(''); setDone('');
    try {
      const r = await fetch(`${API}/api/admin/list-cleanup/scan`, { headers });
      if (!r.ok) throw new Error('Scan failed.');
      const data = await r.json();
      setScan(data);
      setPicked(data.categories.filter((c) => c.count > 0).map((c) => c.key));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!picked.length) return;
    const n = scan.categories.filter((c) => picked.includes(c.key)).reduce((a, c) => a + c.count, 0);
    if (!window.confirm(`Remove ${n} address${n === 1 ? '' : 'es'} from the list? Every one is written to the removal log.`)) return;
    setRemoving(true); setError('');
    try {
      const r = await fetch(`${API}/api/admin/list-cleanup/remove`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ categories: picked }),
      });
      if (!r.ok) throw new Error('Removal failed.');
      const data = await r.json();
      setDone(`${data.removed} removed from the list.`);
      setScan(null);
      setPicked([]);
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setRemoving(false);
    }
  };

  const downloadCsv = () => {
    const rows = [['category', 'email']];
    scan.categories.forEach((c) => c.emails.forEach((e) => rows.push([c.label, e])));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'outlaw-list-cleanup.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggle = (key) =>
    setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  const pickedCount = scan
    ? scan.categories.filter((c) => picked.includes(c.key)).reduce((a, c) => a + c.count, 0)
    : 0;

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 space-y-3" data-testid="list-cleanup-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-xs font-black uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-red-500" /> List clean-up
        </h3>
        <button
          type="button"
          onClick={runScan}
          disabled={busy}
          data-testid="list-cleanup-scan"
          className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 disabled:opacity-50 text-white text-[11px] font-black uppercase tracking-[0.25em] px-3 py-2 rounded"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {busy ? 'Scanning…' : 'Scan the list'}
        </button>
      </div>
      <p className="text-zinc-400 text-xs leading-relaxed">
        Checks every subscriber, unconfirmed sign-up and free account: nonsense or keyboard-mash
        addresses, misspelt providers (gmail.co), dead domains, throwaway services, bounces and
        sign-ups that never confirmed. Paying and comp&apos;d accounts are never removed.
      </p>

      {error && <p className="text-red-400 text-xs" data-testid="list-cleanup-error">{error}</p>}
      {done && <p className="text-emerald-400 text-xs" data-testid="list-cleanup-done">{done}</p>}

      {scan && (
        <div className="space-y-2" data-testid="list-cleanup-results">
          <p className="text-[11px] text-zinc-500 uppercase tracking-[0.2em]">
            {scan.flagged_total} flagged of {scan.total_subscribers} on the list
          </p>
          {scan.categories.map((c) => (
            <div key={c.key} className="rounded border border-zinc-800 bg-black/50">
              <label className="flex items-center gap-3 px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={picked.includes(c.key)}
                  onChange={() => toggle(c.key)}
                  disabled={!c.count}
                  data-testid={`list-cleanup-pick-${c.key}`}
                  className="accent-red-600 h-3.5 w-3.5"
                />
                <span className="text-sm text-white flex-1">{c.label}</span>
                <span className={`text-sm font-black ${c.count ? 'text-red-400' : 'text-zinc-600'}`}>
                  {c.count}
                </span>
                {c.count > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setOpen(open === c.key ? null : c.key); }}
                    className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
                  >
                    {open === c.key ? 'Hide' : 'Show'}
                  </button>
                )}
              </label>
              {open === c.key && (
                <ul className="px-3 pb-3 max-h-40 overflow-auto space-y-0.5">
                  {c.emails.map((e) => (
                    <li key={e} className="font-mono text-[11px] text-zinc-400 break-all">{e}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="flex items-center gap-2 flex-wrap pt-1">
            <button
              type="button"
              onClick={remove}
              disabled={removing || !pickedCount}
              data-testid="list-cleanup-remove"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[11px] font-black uppercase tracking-[0.25em] px-4 py-2 rounded"
            >
              {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Remove {pickedCount || ''}
            </button>
            <button
              type="button"
              onClick={downloadCsv}
              data-testid="list-cleanup-csv"
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-[11px] font-black uppercase tracking-[0.25em] px-4 py-2 rounded"
            >
              Download CSV first
            </button>
          </div>
          <p className="text-[11px] text-zinc-600 flex items-start gap-1.5">
            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
            Removals are permanent, but each one is recorded in the removal log below with the
            reason.
          </p>
        </div>
      )}
    </div>
  );
};

export default ListCleanupCard;
